import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Radio, Space, Divider, Tag } from 'antd';
import { Zap, Send, CheckCircle, XCircle, MessageSquare, User } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; 

export default function WorkflowActionModal({ moduleName, refId, currentStatus, onActionComplete }) {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
    
    // 🎯 متغير ذكي للخط عشان نحقنه في العناصر
    const fontClass = isRTL ? 'font-cairo' : 'font-sans';
    const customFontFamily = isRTL ? 'Cairo, sans-serif' : 'inherit';

    const [isVisible, setIsVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [wfContext, setWfContext] = useState(null); 
    const [form] = Form.useForm();

    const checkWorkflowStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/workflow/check-runtime/${moduleName}/${refId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setWfContext(res.data);
        } catch (err) {
            console.error("Workflow Check Failed", err);
        }
    };

    useEffect(() => {
        if (refId) checkWorkflowStatus();
    }, [refId, currentStatus]);

    const handleAction = async (values) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/workflow/execute-action`, {
                module: moduleName,
                ref_id: refId,
                action: values.action, 
                memo: values.memo,
                instance_id: wfContext.instance_id
            }, { headers: { 'Authorization': `Bearer ${token}` } });

            toast.success(t('workflowAction.successMove')); 
            setIsVisible(false);
            if (onActionComplete) onActionComplete(); 
        } catch (err) {
            toast.error(err.response?.data?.detail || t('workflowAction.failedMove')); 
        } finally {
            setLoading(false);
        }
    };

    if (!wfContext?.can_action) return null;

    return (
        <>
            <Button 
                onClick={() => setIsVisible(true)}
                style={{ fontFamily: customFontFamily }}
                className={`flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white border-0 h-8 shadow-lg animate-pulse ${fontClass} ${isRTL ? 'dir-rtl' : ''}`}
            >
                <Zap size={14} fill="currentColor" />
                <span className={`text-[11px] font-black uppercase ${fontClass}`}>{t('workflowAction.btnRoute')}</span>
            </Button>

            <Modal
                title={
                    <div className={`flex items-center gap-2 text-orange-600 font-black ${fontClass} ${isRTL ? 'text-right' : ''}`}>
                        <Zap size={20} fill="currentColor" />
                        <span style={{ fontFamily: customFontFamily }}>{t('workflowAction.modalTitle')}: {wfContext.current_step?.name}</span>
                    </div>
                }
                open={isVisible} 
                onCancel={() => setIsVisible(false)}
                footer={null}
                width={500}
                rootClassName={`${isRTL ? 'dir-rtl' : 'dir-ltr'}`} 
                className={`${fontClass}`}
                style={{ fontFamily: customFontFamily }}
                destroyOnClose
            >
                <div className={`mb-6 p-4 bg-slate-50 rounded-xl border border-slate-100 ${fontClass}`} style={{ fontFamily: customFontFamily }}>
                    <div className={`flex items-center gap-2 mb-2 text-slate-400 text-[10px] font-black uppercase tracking-widest ${isRTL ? 'text-right' : ''}`}>
                        <User size={12} /> {t('workflowAction.assignedAs')} <Tag color="blue" style={{ fontFamily: customFontFamily, margin: isRTL ? '0 8px 0 0' : '0 0 0 8px' }}>{wfContext.your_role}</Tag>
                    </div>
                    <div className={`text-slate-600 font-bold text-sm italic px-2 ${isRTL ? 'border-r-4 border-l-0 text-right' : 'border-l-4 border-r-0 text-left'} border-orange-400`}>
                        "{wfContext.current_step?.instructions || t('workflowAction.defaultInstruction')}"
                    </div>
                </div>

                <Form form={form} layout="vertical" onFinish={handleAction} className={`${fontClass}`} style={{ fontFamily: customFontFamily }}>
                    <Form.Item 
                        name="action" 
                        label={<span className={`font-black text-[11px] uppercase text-slate-400 ${fontClass}`}>{t('workflowAction.lblChoosePath')}</span>}
                        rules={[{ required: true, message: <span className={fontClass}>{t('workflowAction.valSelectPath')}</span> }]} 
                    >
                        <Radio.Group className="w-full">
                            <Space direction="vertical" className="w-full">
                                <Radio.Button value="TRUE" className={`w-full h-12 flex items-center justify-center rounded-lg border-2 hover:border-green-500 ${fontClass}`} style={{ fontFamily: customFontFamily }}>
                                    <div className={`flex items-center gap-2 text-green-600 font-black`}>
                                        <CheckCircle size={16} /> {t('workflowAction.optApprove')}
                                    </div>
                                </Radio.Button>
                                <Radio.Button value="FALSE" className={`w-full h-12 flex items-center justify-center rounded-lg border-2 hover:border-red-500 mt-2 ${fontClass}`} style={{ fontFamily: customFontFamily }}>
                                    <div className={`flex items-center gap-2 text-red-600 font-black`}>
                                        <XCircle size={16} /> {t('workflowAction.optReject')}
                                    </div>
                                </Radio.Button>
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item 
                        name="memo" 
                        label={<span className={`font-black text-[11px] uppercase text-slate-400 ${fontClass}`}>{t('workflowAction.lblMemo')}</span>}
                    >
                        <Input.TextArea 
                            rows={4} 
                            placeholder={t('workflowAction.placeholderMemo')} 
                            className={`rounded-lg font-bold ${fontClass}`}
                            style={{ fontFamily: customFontFamily }}
                        />
                    </Form.Item>

                    <Divider className="my-4" />

                    <div className={`flex gap-2 `}>
                        <Button onClick={() => setIsVisible(false)} className={`rounded-lg font-bold ${fontClass}`} style={{ fontFamily: customFontFamily }}>
                            {t('workflowAction.btnCancel')}
                        </Button>
                        <Button 
                            loading={loading} 
                            type="primary" 
                            htmlType="submit" 
                            className={`bg-[#1a3353] border-0 rounded-lg font-black flex items-center justify-center gap-2 ${fontClass}`}
                            style={{ fontFamily: customFontFamily }}
                        >
                            <Send size={14} className={isRTL ? 'rotate-180' : ''} /> 
                            <span>{t('workflowAction.btnComplete')}</span>
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
}